/* eslint-disable no-restricted-globals */
import React, { useContext, useEffect } from 'react';
import * as R from 'ramda';
import Snackbar from '@mui/material/Snackbar';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PaymentApp from './PaymentApp';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import MuiAlert from '@mui/material/Alert';
import { StateProvider, store, useStore } from './store';
import FullScreenDialog from './FullScreenDialog';
import TabsCD from '../component/TabsCD';
import Footer from './Footer';
import { useRouteMatch } from 'react-router-dom';
import { Router, useHistory } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import cdlogo from '../cdlogo.svg';
import Copyright from './Copyright';
import { postJSONPS, either, renameKeys } from '@cd/utils';
import { XPropsProvider, withXprops, useXProps } from '../xprops';
import { SecureApp } from './SecureApp';
import useWidgets from './fetch/useWidgets';
import { CenterProgress } from '../component/CenterProgress';
import { QueryClient, QueryClientProvider } from '@cd/react-query';
import { ThemeProviderWithModes } from '../ThemeProviderWithModes';
import { useQuery } from '@cd/react-query';
import { CircularProgress } from '@mui/material';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false
		}
	}
});

const appBarSx = {
	position: 'relative'
};
const appBarImageSx = {
	width: '42px',
	height: 'auto',
	marginRight: '8px'
};
const toolbarSx = {
	display: 'flex',
	alignItems: 'center'
};

const Alert = React.forwardRef(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const XPROPS = [
	'childRoute',
	'childRouteSearch',
	'APP_ID',
	'onAgreementSigned',
	'onLoaded',
	'onParentRouteChange',
	'parentHistory'
];
const enhance = R.compose(SecureApp, withXprops(XPROPS));

const getTabScheme = R.curry(
	(APP_ID, TABS) =>
		(APP_ID &&
			R.compose(
				R.map(renameKeys({ widgetName: 'name' })),
				R.sortBy(R.prop('widgetOrder')),
				R.filter(({ isWidget }) => !APP_ID || (APP_ID && isWidget))
			)(TABS)) ||
		TABS
);

const App = enhance(
	({ childRoute, childRouteSearch, onParentRouteChange, APP_ID }) => {
		const {
			dispatch,
			state: {
				fullScreenDialog,
				error: { show, e, severity }
			}
		} = useContext(store);

		const {
			params: { routeName }
		} = useRouteMatch('/:routeName/:navRouteName?/:param1?/:param2?');

		const { isLoading, isError, data: TABS, error } = useWidgets(dispatch);

		if (isLoading) return <CenterProgress />;

		if (isError) return 'An error has occurred:' + error.message;

		function onTabChange(route) {
			onParentRouteChange(`${route}`);
		}

		const handleClose = (event, reason) => {
			if (reason === 'clickaway') {
				return;
			}

			dispatch({ type: 'hide error' });
		};

		return (
			<>
				<CssBaseline />
				{!APP_ID && (
					<AppBar position="absolute" sx={appBarSx}>
						<Toolbar sx={toolbarSx}>
							<Box
								component="img"
								src={cdlogo}
								sx={appBarImageSx}
								alt="logo"
							/>
							<div>
								<Typography
									variant="h6"
									color="inherit"
									noWrap>
									CYBERDEV CORPORATION
								</Typography>
								<Typography
									variant="caption"
									display="block"
									color="inherit"
									noWrap>
									UNION CLOUD SOFTWARE
								</Typography>
							</div>
						</Toolbar>
					</AppBar>
				)}
				<TabsCD
					APP_ID={APP_ID}
					childRoute={childRoute}
					tabs={getTabScheme(APP_ID, TABS)}
					onTabChange={onTabChange}
					initialValue={APP_ID ? childRoute : `/${routeName}`}
					urlMaskRegEx={/^\/[a-z0-9]*(\/[a-z0-9]*)\/?/}
				/>
				<div style={{ textAlign: 'right', fontSize: '10px' }}>
					{/* TODO: Make debug only 
					<div>cr: {childRoute}</div>
					<div>path: {location.pathname}</div>*/}
				</div>
				<PaymentApp
					childRoute={childRoute}
					childRouteSearch={childRouteSearch}
					APP_ID={APP_ID}
					codename="WhereIsMyMoney"
				/>
				<FullScreenDialog {...fullScreenDialog} />
				<Snackbar
					sever
					open={show}
					autoHideDuration={6000}
					onClose={handleClose}
					action={
						<IconButton
							size="small"
							aria-label="close"
							color="inherit"
							onClick={handleClose}>
							<CloseIcon fontSize="small" />
						</IconButton>
					}>
					<Alert onClose={handleClose} severity={severity}>
						{R.is(String, e)
							? e
							: e?.message || e?.where || 'Unknown error'}
					</Alert>
				</Snackbar>
				{!APP_ID && <Footer />}
				{!APP_ID && <Copyright />}
			</>
		);
	}
);

const withXPropsStaticRouter = withXprops(XPROPS);

/**
 * ZOID will wrap every function of the history object in a wrapper that resolves to a Zalgo Promise
 * Noramlly the listen function returns a function which must be called
 * to avoid a memory leak.
 *
 * @param {*} parentHistory
 * @returns parentHistory
 */
function fixListenFunction(parentHistory) {
	const origListen = parentHistory.listen;
	parentHistory.listen = fn => {
		// call zoid wrapper
		const unlisten = origListen(fn);
		return async () => {
			(await unlisten)();
		};
	};
	return parentHistory;
}

function useGetSignAgreementCheck(onAgreementSigned) {
	return useQuery(['signAgreementCheck'], () =>
		postJSONPS('POST', `/agreement/check`, {}).then(
			either(console.log, ({ count }) => {
				const needsToSign = !!+count;
				onAgreementSigned(needsToSign);
				return {
					needsToSign
				};
			})
		)
	);
}

const StaticRouterX = withXPropsStaticRouter(
	({ onLoaded, children, parentHistory }) => {
		useEffect(() => {
			onLoaded();
		}, []);

		return (
			<Router key={1} history={fixListenFunction(parentHistory)}>
				{children}
			</Router>
		);
	}
);

const AppWrap = ({ children }) => {
	const { dispatch } = useStore();
	const { onAgreementSigned } = useXProps();
	const { isFetching, isFetched, data } =
		useGetSignAgreementCheck(onAgreementSigned);
	const history = useHistory();

	useEffect(() => {
		if (!data) return;

		dispatch({
			type: 'set agreement status',
			sign: data.needsToSign
		});
		if (data.needsToSign) {
			history.push('/account/legal/list');
		}
	}, [data]);

	if (!isFetched || isFetching) {
		return <CircularProgress />;
	}
	return children;
};

const StateApp = () => (
	<StateProvider>
		<XPropsProvider>
			<QueryClientProvider client={queryClient}>
				<StaticRouterX>
					<ThemeProviderWithModes>
						<AppWrap>
							<App />
						</AppWrap>
					</ThemeProviderWithModes>
				</StaticRouterX>
			</QueryClientProvider>
		</XPropsProvider>
	</StateProvider>
);

export default StateApp;
